import each from 'lodash/each';
import omit from 'lodash/omit';
import defer from 'lodash/defer';
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';
import indexOf from 'lodash/indexOf';
import map from 'lodash/map';
import pick from 'lodash/pick';
import { produce } from 'immer';
import includes from 'lodash/includes';

import loglevel from 'loglevel';

import { getIdFrom } from 'zoomforth_codesbane_viewer/lib/id_utils.js';

import { get as getPageHistoryTracker } from 'zoomforth_zf/appglobals/page-history-tracker.js';
import { get as getIntercomTrackerStore } from 'zoomforth_zf/appglobals/intercom-tracker.js';

import { showErrorNotification } from './misc.js';

import {
  getPageId,
  getEditorOnlySectionOverrides,
  getActiveTabId
} from 'zoomforth_codesbane_editor/reducers/index.js';

import {
  isVideoishAssetType
} from 'zoomforth_codesbane_viewer/lib/page_content_utils.js';

import {
  scrollToSection
} from 'zoomforth_codesbane_viewer/action_creators/viewer.js';

import {
  showModal
} from './editor_modals.js';

import {
  generateSectionId
} from 'zoomforth_codesbane_editor/lib/id_generators.js';

import {
  generateNewSectionData
} from 'zoomforth_codesbane_editor/lib/sections.js';

import {
  generateInitialSectionOverridesForSection
} from 'zoomforth_codesbane_editor/lib/section_overrides/index.js';

import {
  getSectionById,
  getSubpageForSection,
  getSubpageDisplayName,
  getMenuItemsForSection,
  getSectionBackgroundData,
  getSectionFallbackBackgroundData,
  getSectionBorderData,
  getSerializableSectionAndContentData,
  getSectionSingletonContentItem,
} from 'zoomforth_codesbane_viewer/reducers/index.js';

import {
  setClipboardData,
  fetchClipboardDataForType
} from './user_clipboard.js';

import {
  ensureThrottledPageConfigSaving
} from './config.js';

import {
  createTextContentItem,
  createJobFeedContentItem,
  createAccordionContentItems,
  createTabsContentItems,
  createSectionBackgroundImageContent,
  createSectionBackgroundVideoContent
} from './content.js';

import {
  createContentItemFromContentForGridSection,
  deleteGridSection
} from './sections.grid.js';

import {
  deleteOrderedContentSection,
  createContentItemFromContentForOrderedContentSection
} from './sections.ordered_content.js';

import {
  deleteSingletonContentSection,
  createContentItemFromContentForSingletonContentSection
} from './sections.singleton.js';

import {
  getDisplayName,
  getMarginData,
  getPaddingData,
  isSingletonContentSection,
  isOrderedContentSection,
  isGridPositionsContentSection
} from 'zoomforth_codesbane_viewer/lib/sections.js';

import {
  SECTION_TYPE_JOB_FEED,
  SECTION_TYPE_ACCORDION,
  SECTION_TYPE_TABS,
  SECTION_TYPE_TEXT
} from 'zoomforth_codesbane_viewer/const/sections.js';

import ConfirmDeleteSectionModal
  from 'zoomforth_codesbane_editor/exterior_editor/editor_modals/confirm_delete_section_container.jsx';

import ConfirmDeleteMenuItemForSectionModal
  from 'zoomforth_codesbane_editor/exterior_editor/editor_modals/confirm_delete_menuitem_for_section_container.jsx';

import ConfirmClearTabContentModal
  from 'zoomforth_codesbane_editor/exterior_editor/editor_modals/confirm_clear_tab_content_container.jsx';

import ConfirmCreatePdfForSectionModal
  from 'zoomforth_codesbane_editor/exterior_editor/editor_modals/confirm_create_pdf_for_section_container.jsx';

import ConfirmCreatePresentationForSectionModal
  from 'zoomforth_codesbane_editor/exterior_editor/editor_modals/confirm_create_presentation_for_section_container.jsx';

import ConfirmCreateHtmlPackageForSectionModal
  from 'zoomforth_codesbane_editor/exterior_editor/editor_modals/confirm_create_html_package_for_section_container.jsx';

import IframeEmbedSectionCreatorModal
  from 'zoomforth_codesbane_editor/exterior_editor/editor_modals/iframe_embed_section_creator_container.jsx';

import {
  CLIPBOARD_ITEM_TYPE_SECTION
} from 'zoomforth_codesbane_editor/lib/user_clipboard/const.js';

import {
  SECTION_EDITOR_TAB_CONTENT,
  SECTION_EDITOR_TAB_STYLING,
  SECTION_EDITOR_TAB_MOBILE,
  SECTION_EDITOR_TAB_ACTION,
  VALID_SECTION_EDITOR_TABS,
} from 'zoomforth_codesbane_editor/const/const.js';

import {
  SET_SECTION_EDITOR_CURRENT_TAB
} from 'zoomforth_codesbane_editor/action_types.js';


export const beginEditSection = (section, doSetEditingTabId = true) =>
  (dispatch, getState) => {
    if (section._isInsideTab) { return }
    dispatch({
      type: 'EDIT_SECTION',
      section: section
    });

    if (section.type === SECTION_TYPE_TABS) {
      const activeTabId = getActiveTabId(getState(), section.id) || section.tabs[0].id;
      dispatch(setActiveTabId(section.id, activeTabId, doSetEditingTabId));
    }
  };

  export const endEditSection = ()=> {
    return {
      type: 'EDIT_SECTION',
      section: null
    };
  };

export const createSection = ({ sectionType, subpage, atIndex, tab, newSectionId=null })=>
  (dispatch, getState)=> {
    // Prevent creating tabs sections inside tabs
    if (tab && sectionType === SECTION_TYPE_TABS) {
      dispatch(showErrorNotification(
        'Cannot Create Tabs Section',
        'Tabs sections cannot be placed inside tabs. Please create them in a subpage instead.'
      ));
      return;
    }

    const extraSectionData = newSectionId ? { id: newSectionId } : {};
    const newSection = generateNewSectionData(sectionType, extraSectionData);
    const initialSectionOverrides =
      generateInitialSectionOverridesForSection(newSection);

    dispatch({
      type: 'ADD_SECTION',
      section: newSection,
      subpage: subpage,
      index: atIndex,
      tab: tab,
      sectionOverrides: initialSectionOverrides,
      shouldEditSection: true
    });

    // Don't save page config immediately for sections that need content creation
    // The content creation functions will handle saving after adding their content
    // However, always save when adding sections to tabs since they don't need additional content creation
    if (tab || (sectionType !== SECTION_TYPE_TABS && sectionType !== SECTION_TYPE_ACCORDION && sectionType !== SECTION_TYPE_JOB_FEED)) {
      dispatch(ensureThrottledPageConfigSaving());
    }

    getPageHistoryTracker().trackAddSection(
      getSubpageDisplayName(getState(), subpage),
      getDisplayName(newSection)
    );

    switch (sectionType) { 

    // TODO: Change section type inline text here to imports from const/sections.js
    case 'text':
      dispatch(createTextContentItem(newSection));
      break;

    case 'iframe_embed':
      dispatch(showIframeEmbedSectionModal(newSection));
      break;

    case 'pdf': {
      dispatch(showModal(ConfirmCreatePdfForSectionModal, { section: newSection }));
      break;
    }

    case 'presentation':
      dispatch(showModal(ConfirmCreatePresentationForSectionModal, { section: newSection }));
      break;

    case 'html_package':
      dispatch(showModal(ConfirmCreateHtmlPackageForSectionModal, { section: newSection }));
      break;

    case 'videoplaylist':
    case 'slideshow':
    case 'tiles':
      // these cases need no additional processing
      break;

    case SECTION_TYPE_JOB_FEED:
      dispatch(createJobFeedContentItem(newSection));
      break;

    case SECTION_TYPE_ACCORDION:
      dispatch(createAccordionContentItems(newSection));
      break;
    
    case SECTION_TYPE_TABS:
      dispatch(createTabsContentItems(newSection));
      break;

    default:
      throw new Error(`Unexpected section type selected for creation: "${sectionType}"`);
    }

  };

export const setSectionCreatorSectionData = (sectionData = null)=> {
  if (sectionData === null) {
    return {
      type: 'SET_SECTION_CREATOR_SECTION',
      temporarySectionData: {}
    };
  }

  const { subpage, atIndex, tab } = sectionData;

  return {
    type: 'SET_SECTION_CREATOR_SECTION',
    data: {
      section: {
        id: generateSectionId(),
        type: 'sectioncreator'
      },
      subpage: subpage,
      index: atIndex,
      tab: tab
    }
  };
};

export const clearTabContent = (parentSectionId, tabId) => 
  (dispatch) => {
    dispatch({
      type: 'CLEAR_TAB_CONTENT',
      parentSectionId,
      tabId
    });
    
    // Trigger saving when tab content is cleared
    dispatch(ensureThrottledPageConfigSaving());
  };

export const setEditingTabId = (parentSectionId, tabId) => {
  return {
    type: 'SET_EDITING_TAB_ID',
    parentSectionId,
    tabId
  };
};

export const clearEditingTabId = () => {
  return {
    type: 'CLEAR_EDITING_TAB_ID',
    parentSectionId: null,
    tabId: null
  };
};

export const setActiveTabId = (sectionId, tabId, doSetEditingTabId = true) => 
  (dispatch, getState) => {
    const section = getSectionById(getState(), sectionId);
    const tabSectionId = section.tabs.find(t => t.id === tabId)?.content?.sectionId;
    const contentItem = tabSectionId ? getSectionSingletonContentItem(getState(), tabSectionId) : null;
    const contentUuid = contentItem ? contentItem.uuid : null;
    dispatch({
      type: 'SET_ACTIVE_TAB_ID',
      sectionId,
      tabId,
      contentUuids: contentUuid && doSetEditingTabId ? [contentUuid] : null
    });
    if (contentUuid && doSetEditingTabId) {
      dispatch(setEditingTabId(sectionId, tabId));
    }
  };

export const confirmClearTabContent = (parentSectionId, tabId) => 
  (dispatch, getState) => {
    const parentSection = getSectionById(getState(), parentSectionId);
    const tab = parentSection.tabs.find(t => t.id === tabId);
    
    let section = null;
    if (tab.content && tab.content.sectionId) {
      section = getSectionById(getState(), tab.content.sectionId);
    }
    
    dispatch(showModal(ConfirmClearTabContentModal, { 
      parentSectionId, 
      tabId, 
      section, 
      tab 
    }));
  };

/*
  Only to be called by the specific implementation of se
  Simply remove the section object from the config. 
*/
export const removeSection = (sectionOrId)=>
  (dispatch)=> {
    const sectionId = getIdFrom(sectionOrId);
    dispatch({
      type: 'REMOVE_SECTIONS',
      sectionIds: [sectionId]
    });
    dispatch(ensureThrottledPageConfigSaving());
  };


export const confirmDeleteSection = (sectionOrId)=>
  (dispatch, getState)=> {
    const sectionId = getIdFrom(sectionOrId);
    const section = getSectionById(getState(), sectionId);
    const hasMenuItems = getMenuItemsForSection(getState(), sectionId);

    dispatch(showModal(ConfirmDeleteSectionModal, { section, hasMenuItems }));
  };

/*
  Full logic to delete section.

  Calls the specific implementation of section deleting depending
  on section type

*/
export const deleteTabsSection = (sectionOrId)=>
  (dispatch, getState)=> {
    const section = getSectionById(getState(), getIdFrom(sectionOrId));
    const tabs = section.tabs || [];

    tabs.forEach(tab => {
      if (tab.content && tab.content.sectionId) {
        dispatch(removeSection(tab.content.sectionId));
      }
    });
    dispatch(removeSection(section));

  };

export const deleteSection = (sectionOrId)=>
  (dispatch, getState)=> {
    const sectionId = getIdFrom(sectionOrId);
    const section = getSectionById(getState(), sectionId);
    const subpage = getSubpageForSection(getState(), section);

    const menuItemsForSection = getMenuItemsForSection(getState(), section);

    if (!isEmpty(menuItemsForSection)) {
      dispatch(showModal(ConfirmDeleteMenuItemForSectionModal, { menuItemsForSection }));
    }

    getPageHistoryTracker().trackDeleteSection(
      getSubpageDisplayName(getState(), subpage),
      getDisplayName(section)
    );

    if (section.type === SECTION_TYPE_TABS) {
      dispatch(deleteTabsSection(section));

    } else if (isOrderedContentSection(section)) {
      dispatch(deleteOrderedContentSection(section));

    } else if (isGridPositionsContentSection(section)) {
      dispatch(deleteGridSection(section));

    } else if (isSingletonContentSection(section)) {
      dispatch(deleteSingletonContentSection(section));

    } else {
      throw new Error('Cannot delete content from unknown section type');
    }
  };


export const moveSectionUp = (section)=>
  (dispatch)=> {
    dispatch({
      type: 'MOVE_SECTION_UP',
      section: section
    });
    dispatch(ensureThrottledPageConfigSaving());
  };


export const moveSectionDown = (section)=>
  (dispatch)=> {
    dispatch({
      type: 'MOVE_SECTION_DOWN',
      section: section
    });
    dispatch(ensureThrottledPageConfigSaving());
  };


export const setSectionAndOverrides = (sectionOrId, sectionData, sectionOverrides)=>  
  (dispatch, getState)=> {

    const sectionId = getIdFrom(sectionOrId);
    const section = getSectionById(getState(), sectionId);

    dispatch({
      type: 'SET_SECTION_AND_OVERRIDES',
      sectionId,
      section: sectionData,
      sectionOverrides
    });

    if (!isEqual(sectionData, section)) {
      dispatch(ensureThrottledPageConfigSaving());
    }

  };

export const setSectionAnimationProps = (sectionOrId, produceFunction)=>  
  (dispatch, getState)=> {

    const section = getSectionById(getState(), sectionOrId) || sectionOrId;


    dispatch({
      type: 'SET_SECTION_SIMPLE_DATA',
      sectionId: section.id,
      data: { animationSettings: produce(section.animationSettings||{}, produceFunction)}
    });

    dispatch(ensureThrottledPageConfigSaving());
};

export const showIframeEmbedSectionModal = (sectionOrId)=>
  (dispatch, getState)=> {
    const sectionId = getIdFrom(sectionOrId);
    const section = getSectionById(getState(), sectionId);

    dispatch(showModal(IframeEmbedSectionCreatorModal, { section }));

  };


export const setSectionSimpleData = (sectionOrId, data)=>
  (dispatch)=> {
    const sectionId = getIdFrom(sectionOrId);

    dispatch({
      type: 'SET_SECTION_SIMPLE_DATA',
      sectionId,
      data
    });

    dispatch(ensureThrottledPageConfigSaving());
  };

export const updateSectionBackgroundData = (sectionOrId, data, dataKey = 'backgroundData') =>
  (dispatch, getState)=> {
    const sectionId = getIdFrom(sectionOrId);
    const section = getSectionById(getState(), sectionId);
    const backgroundData = omit(getSectionBackgroundData(getState(), section, dataKey), ['contentItem', 'asset']);

    dispatch(setSectionSimpleData(section, {
      [dataKey]: { ...backgroundData, ...data }
    }));
  };

export const setSectionBackgroundData = (sectionOrId, data, key = 'backgroundData') =>
  (dispatch, getState)=> {
    const sectionId = getIdFrom(sectionOrId);
    const section = getSectionById(getState(), sectionId);

    dispatch(setSectionSimpleData(section, {
      [key]: data
    }));
  };

export const updateSectionFallbackBackgroundData = (sectionOrId, data) =>
  (dispatch, getState)=> {
    const sectionId = getIdFrom(sectionOrId);
    const section = getSectionById(getState(), sectionId);
    const fallbackBackgroundData = omit(getSectionFallbackBackgroundData(getState(), section), ['contentItem', 'asset']);

    dispatch(setSectionSimpleData(section, {
      fallbackBackgroundData: { ...fallbackBackgroundData, ...data }
    }));
  };

export const setSectionFallbackBackgroundData = (sectionOrId, data) =>
  (dispatch, getState)=> {
    const sectionId = getIdFrom(sectionOrId);
    const section = getSectionById(getState(), sectionId);

    dispatch(setSectionSimpleData(section, {
      fallbackBackgroundData: data
    }));
  };

export const updateSectionBorderData = (sectionOrId, data, dataKey = 'borderData') =>
  (dispatch, getState)=> {
    const sectionId = getIdFrom(sectionOrId);
    const section = getSectionById(getState(), sectionId);
    const borderData = getSectionBorderData(getState(), section, dataKey);

    dispatch(setSectionSimpleData(section, {
      [dataKey]: { ...borderData, ...data }
    }));
  };

export const setSectionBorderData = (sectionOrId, data, key = 'borderData') =>
  (dispatch, getState)=> {
    const sectionId = getIdFrom(sectionOrId);
    const section = getSectionById(getState(), sectionId);

    dispatch(setSectionSimpleData(section, {
      [key]: data
    }));
  };

/**
 * Create a new section from serializableSectionAndContentData. This will
 * recreate the section object and create new content items for each piece of
 * content.
 *
 * This serializableSectionAndContentData can be generated via the
 * getSerializableSectionAndContentData Redux selector.
 */
export const createSectionFromSerializableSectionAndContent = (
  subpage,
  atIndex,
  serializableSectionAndContentData,
  oldSectionId=null,
  {
    shouldScrollAndEditSection=true,
    tab=null
  }={}
) =>
  (dispatch, getState)=> {
    const {
      originalSectionData,
      contentItems,
      backgroundAsset,
      fallbackBackgroundAsset
    } = serializableSectionAndContentData;

    const extraSectionData = originalSectionData;

    if (isGridPositionsContentSection(originalSectionData)) {
      extraSectionData.gridPositions = map(contentItems,
        (contentItem) => (
          pick(contentItem, ['sizex', 'sizey', 'row', 'col'])
        )
      );
    }

    var newSection = generateNewSectionData(
      originalSectionData.type,
      extraSectionData
    );

    newSection = omit(newSection, ['originalSectionId']);

    var oldSectionOverrides = {};

    if (oldSectionId) {
      oldSectionOverrides = getEditorOnlySectionOverrides(getState(), oldSectionId);
    }

    const initialSectionOverrides = generateInitialSectionOverridesForSection(
      newSection,
      oldSectionOverrides,
      {
        addTileCreatorForGrid: true,
        /**
         * We do this below by inserting content
         * tiles and immediately replacing them
         */
        shouldMapGridPositionsToEidMask: false,
        shouldMaskOverridesWithPredefinedSection: false
      }
    );

    dispatch({
      type: 'ADD_SECTION',
      section: newSection,
      subpage: subpage,
      index: atIndex,
      tab: tab,
      sectionOverrides: initialSectionOverrides,
      shouldEditSection: shouldScrollAndEditSection
    });

    dispatch(ensureThrottledPageConfigSaving());

    if (shouldScrollAndEditSection) {
      // we defer this so we wait until the section is created in the DOM
      defer(() => { dispatch(scrollToSection(newSection.id)); });
    }

    if (isSingletonContentSection(newSection)) {
      dispatch(createContentItemFromContentForSingletonContentSection(newSection, contentItems[0], { shouldEditOnCreate: false }));

    } else if (isOrderedContentSection(newSection)) {

      each(contentItems, (contentItem, idx)=> {
        dispatch(createContentItemFromContentForOrderedContentSection(newSection, contentItem, { atIndex: idx }, { shouldEditOnCreate: false }));
      });

    } else if (isGridPositionsContentSection(newSection)) {
      each(contentItems, (contentItem)=> {
        const {
          sizex,
          sizey,
          row,
          col
        } = contentItem;

        const positionData = {
          sizex, sizey, row, col
        };

        const postCreateData = {
          shouldCreateAtEnd: false,
          shouldCreateTileCreatorAtPosition: true,
          shouldEditOnCreate: false,
          shouldScrollToOnCreate: false,
          canShowUserCropModal: false
        };

        dispatch(createContentItemFromContentForGridSection(
          newSection,
          contentItem,
          positionData,
          postCreateData
        ));
      });
    }

    // background images and videos are not handled by normal creation so we need to
    // explicitly recreate it if the section needs it
    if (backgroundAsset) {
      if (backgroundAsset.assetType === 'photo') {
        dispatch(createSectionBackgroundImageContent(newSection, backgroundAsset));

      } else if (isVideoishAssetType(backgroundAsset.assetType)) {
        dispatch(createSectionBackgroundVideoContent(newSection, backgroundAsset));

      }
    }

    // fallbacks apply to video sections currently
    if (fallbackBackgroundAsset) {
      if (fallbackBackgroundAsset.assetType === 'photo') {
        dispatch(createSectionBackgroundImageContent(newSection, fallbackBackgroundAsset, /* isFallback = */ true));

      } else if (isVideoishAssetType(fallbackBackgroundAsset.assetType)) {
        dispatch(createSectionBackgroundVideoContent(newSection, fallbackBackgroundAsset, /* isFallback = */ true));

      }
    }
  };

export const copySection = (sectionOrId, type = CLIPBOARD_ITEM_TYPE_SECTION)=>
  (dispatch, getState)=> {
    const serializableSectionAndContentData = 
      getSerializableSectionAndContentData(getState(), sectionOrId);

    dispatch(setClipboardData(
      type,
      serializableSectionAndContentData
    ));
    getIntercomTrackerStore().trackPageEditorCopySection(getPageId(getState()));
  };

export const pasteSection = (subpage, atIndex, tab = null)=>
  (dispatch, getState)=>  {
    dispatch(fetchClipboardDataForType(CLIPBOARD_ITEM_TYPE_SECTION, (serializableSectionAndContentData)=> {
      if (!serializableSectionAndContentData) {
        loglevel.error('No section data found to paste -- paste was enabled though');
        dispatch(showErrorNotification(
          'Clipboard Error',
          'Could not retrieve section data from your clipboard.'
        ));
        return;
      }

      const { originalSectionData, nestedSectionsData, backgroundAsset, fallbackBackgroundAsset } = serializableSectionAndContentData;
      const { originalSectionId } = originalSectionData;

      // Prevent pasting tabs sections into tabs
      if (tab && originalSectionData.type === SECTION_TYPE_TABS) {
        dispatch(showErrorNotification(
          'Cannot Paste Tabs Section',
          'Tabs sections cannot be placed inside a tab.'
        ));
        return;
      }

      if (!nestedSectionsData || nestedSectionsData.length === 0) {
        dispatch(createSectionFromSerializableSectionAndContent(
          subpage,
          atIndex,
          serializableSectionAndContentData,
          originalSectionId, // oldSectionId
          {
            shouldScrollAndEditSection: true,
            tab: tab
          }
        ));

        if (tab) {
          dispatch(setSectionCreatorSectionData(null));

          const parentSection = getSectionById(getState(), tab.parentSectionId);
          if (parentSection) {
            dispatch(setActiveTabId(tab.parentSectionId, tab.id));
            dispatch(setEditingTabId(tab.parentSectionId, tab.id));
            dispatch(beginEditSection(parentSection));
          }
        }
      }
      else {
        const newSectionId = generateSectionId();
        dispatch(createSection({
          sectionType: SECTION_TYPE_TABS,
          subpage: subpage,
          atIndex: atIndex,
          tab: tab,
          newSectionId: newSectionId
        }));
        

        // 2. For each tab in original section, create the section content
        
        const emptyTabs = originalSectionData.tabs.map(tab => ({
          id: tab.id,
          title: tab.title,
          content: {
            section: null,
            sectionId: null
          }
        }));

        const additionalSectionData = {
          tabs: emptyTabs
        };

        additionalSectionData.layoutSettings = originalSectionData.layoutSettings || {};
        additionalSectionData.innerBackgroundData = originalSectionData.innerBackgroundData || {};
        additionalSectionData.backgroundData = originalSectionData.backgroundData || {};
        additionalSectionData.fallbackBackgroundData = originalSectionData.fallbackBackgroundData || {};
        additionalSectionData.borderData = originalSectionData.borderData || {};

        dispatch({
          type: 'SET_SECTION_SIMPLE_DATA',
          sectionId: newSectionId,
          data: additionalSectionData
        });
          
        // Then, add sections to each tab
        nestedSectionsData.forEach((nestedSectionData, index) => {
          const tabId = originalSectionData.tabs[index]?.id;
          
          if (tabId) {
            dispatch(createSectionFromSerializableSectionAndContent(
              null, // subpage
              null, // atIndex
              nestedSectionData,
              null, // oldSectionId
              {
                shouldScrollAndEditSection: false,
                tab: {
                  id: tabId,
                  parentSectionId: newSectionId
                }
              }
            ));
          }
        });

        const newSection = { id: newSectionId };
        if (backgroundAsset) {
          if (backgroundAsset.assetType === 'photo') {
            dispatch(createSectionBackgroundImageContent(newSection, backgroundAsset));
          } else if (isVideoishAssetType(backgroundAsset.assetType)) {
            dispatch(createSectionBackgroundVideoContent(newSection, backgroundAsset));
          }
        }

        if (fallbackBackgroundAsset) {
          if (fallbackBackgroundAsset.assetType === 'photo') {
            dispatch(createSectionBackgroundImageContent(newSection, fallbackBackgroundAsset, /* isFallback = */ true));
          } else if (isVideoishAssetType(fallbackBackgroundAsset.assetType)) {
            dispatch(createSectionBackgroundVideoContent(newSection, fallbackBackgroundAsset, /* isFallback = */ true));
          }
        }
      }

      getIntercomTrackerStore().trackPageEditorPasteSection(getPageId(getState()));
    }));
  };

export const pasteMultipleCopiedSectionsDataIntoSubpage = (subpage, atIndex, clipboardItems, tab = null) =>
  (dispatch, getState) =>  {
      if (!clipboardItems) {
        loglevel.error('No section data found to paste -- paste was enabled though');
        dispatch(showErrorNotification(
          'Clipboard Error',
          'Could not retrieve section data from your clipboard.'
        ));
        return;
      }


      each(clipboardItems, ({ data }, idx) => {
        const { originalSectionData, nestedSectionsData, backgroundAsset, fallbackBackgroundAsset } = data;
        const { originalSectionId } = originalSectionData;
        const currentIndex = atIndex + idx;

        if (tab && originalSectionData.type === SECTION_TYPE_TABS) {
          dispatch(showErrorNotification(
            'Cannot Paste Tabs Section',
            'Tabs sections cannot be placed inside a tab.'
          ));
          return;
        }

        if (!nestedSectionsData || nestedSectionsData.length === 0) {
          dispatch(createSectionFromSerializableSectionAndContent(
            subpage,
            currentIndex,
            data,
            originalSectionId, // oldSectionId
            {
              shouldScrollAndEditSection: true,
              tab: tab
            }
          ));
        }
        else {
          const newSectionId = generateSectionId();
          dispatch(createSection({
            sectionType: SECTION_TYPE_TABS,
            subpage: subpage,
            atIndex: currentIndex,
            tab: tab,
            newSectionId: newSectionId
          }));
          
          // 2. For each tab in original section, create the section content
          
          const emptyTabs = originalSectionData.tabs.map(tab => ({
            id: tab.id,
            title: tab.title,
            content: {
              section: null,
              sectionId: null
            }
          }));

          const additionalSectionData = {
            tabs: emptyTabs
          };

          additionalSectionData.layoutSettings = originalSectionData.layoutSettings || {};
          additionalSectionData.innerBackgroundData = originalSectionData.innerBackgroundData || {};
          additionalSectionData.backgroundData = originalSectionData.backgroundData || {};
          additionalSectionData.fallbackBackgroundData = originalSectionData.fallbackBackgroundData || {};
          additionalSectionData.borderData = originalSectionData.borderData || {};

          dispatch({
            type: 'SET_SECTION_SIMPLE_DATA',
            sectionId: newSectionId,
            data: additionalSectionData
          });
            
          // Then, add sections to each tab
          nestedSectionsData.forEach((nestedSectionData, nestedIndex) => {
            const tabId = originalSectionData.tabs[nestedIndex]?.id;
            
            if (tabId) {
              dispatch(createSectionFromSerializableSectionAndContent(
                null, // subpage
                null, // atIndex
                nestedSectionData,
                null, // oldSectionId
                {
                  shouldScrollAndEditSection: false,
                  tab: {
                    id: tabId,
                    parentSectionId: newSectionId
                  }
                }
              ));
            }
          });

          // Restore background assets (background images and videos are not handled by normal creation)
          const newSection = { id: newSectionId };
          if (backgroundAsset) {
            if (backgroundAsset.assetType === 'photo') {
              dispatch(createSectionBackgroundImageContent(newSection, backgroundAsset));
            } else if (isVideoishAssetType(backgroundAsset.assetType)) {
              dispatch(createSectionBackgroundVideoContent(newSection, backgroundAsset));
            }
          }

          if (fallbackBackgroundAsset) {
            if (fallbackBackgroundAsset.assetType === 'photo') {
              dispatch(createSectionBackgroundImageContent(newSection, fallbackBackgroundAsset, /* isFallback = */ true));
            } else if (isVideoishAssetType(fallbackBackgroundAsset.assetType)) {
              dispatch(createSectionBackgroundVideoContent(newSection, fallbackBackgroundAsset, /* isFallback = */ true));
            }
          }
        }

        getIntercomTrackerStore().trackPageEditorPasteSection(getPageId(getState()));
      });
  };

export const duplicateSection = (sectionOrId)=>
  (dispatch, getState)=> {
    const sectionId = getIdFrom(sectionOrId);
    const section = getSectionById(getState(), sectionId);
    const sectionSubpage = getSubpageForSection(getState(), section);
    const newSectionIdx = indexOf(sectionSubpage.orderedSectionIds, sectionId);

    const serializableSectionAndContentData = getSerializableSectionAndContentData(getState(), sectionOrId);

    dispatch(createSectionFromSerializableSectionAndContent(
      sectionSubpage,
      newSectionIdx + 1,
      serializableSectionAndContentData,
      sectionId // oldSectionId
    ));

    getIntercomTrackerStore().trackPageEditorDuplicateSection(getPageId(getState()));
  };

export const updateSectionTileSpacing = (sectionOrId, tileSpacingData) =>
  (dispatch, getState)=> {
    const sectionId = getIdFrom(sectionOrId);
    const section = getSectionById(getState(), sectionId);

    dispatch(setSectionSimpleData(section, tileSpacingData));
  };

export const updateSectionMarginData = (sectionOrId, data) =>
  (dispatch, getState)=> {
    const sectionId = getIdFrom(sectionOrId);
    const section = getSectionById(getState(), sectionId);
    const marginData = getMarginData(section);

    dispatch(setSectionSimpleData(section, {
      marginData: { ...marginData, ...data }
    }));
  };

export const updateSectionPaddingData = (sectionOrId, data) =>
  (dispatch, getState)=> {
    const sectionId = getIdFrom(sectionOrId);
    const section = getSectionById(getState(), sectionId);
    const paddingData = getPaddingData(section);

    dispatch(setSectionSimpleData(section, {
      paddingData: { ...paddingData, ...data }
    }));
  };

export const setSectionEditorCurrentTab = (currentTab)=> {
  if (!includes(VALID_SECTION_EDITOR_TABS, currentTab)) {
    throw new Error(`Not a valid tab ${currentTab}`);
  }

  return { type: SET_SECTION_EDITOR_CURRENT_TAB, currentTab };
};
